import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../../../shared/api/api";
import AlertMessage from "../../../shared/components/AlertMessage";
import { useAutoDismissMessage } from "../../../shared/hooks/useAutoDismissMessage";

const PROFILE_ENDPOINT = "/usuarios.php?action=profile";
const CHANGE_PASSWORD_ENDPOINT = "/usuarios.php?action=changePassword";
const UPDATE_PHOTO_ENDPOINT = "/usuarios.php?action=updatePhoto";
const DELETE_PHOTO_ENDPOINT = "/usuarios.php?action=deletePhoto";
const MAX_PROFILE_PHOTO_SIZE = 2 * 1024 * 1024;
const MAX_PROFILE_PHOTO_WIDTH = 1200;
const MAX_PROFILE_PHOTO_HEIGHT = 1200;
const CURRENT_PROFILE_PHOTO_KEY = "profilePhoto:current";
const PHOTO_EDITOR_SIZE = 240;
const PHOTO_EXPORT_SIZE = 512;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const clampPhotoEditor = (editor) => {
  if (!editor?.width || !editor?.height) return editor;

  const baseScale = Math.max(PHOTO_EDITOR_SIZE / editor.width, PHOTO_EDITOR_SIZE / editor.height);
  const displayedWidth = editor.width * baseScale * editor.zoom;
  const displayedHeight = editor.height * baseScale * editor.zoom;
  const maxOffsetX = Math.max(0, (displayedWidth - PHOTO_EDITOR_SIZE) / 2);
  const maxOffsetY = Math.max(0, (displayedHeight - PHOTO_EDITOR_SIZE) / 2);

  return {
    ...editor,
    offsetX: clamp(editor.offsetX, -maxOffsetX, maxOffsetX),
    offsetY: clamp(editor.offsetY, -maxOffsetY, maxOffsetY)
  };
};

export default function Perfil() {
  const { apiFetch } = useApi();
  const cargaInicialEjecutada = useRef(false);
  const [perfil, setPerfil] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardandoFoto, setGuardandoFoto] = useState(false);
  const [fotoPerfil, setFotoPerfil] = useState("");
  const [fotoPreview, setFotoPreview] = useState("");
  const [fotoEditor, setFotoEditor] = useState(null);
  const [showActual, setShowActual] = useState(false);
  const [showNueva, setShowNueva] = useState(false);
  const [editorDrag, setEditorDrag] = useState(null);
  const [form, setForm] = useState({
    passwordActual: "",
    passwordNueva: "",
    confirmarPassword: ""
  });

  useAutoDismissMessage(mensaje, () => setMensaje(null));

  const cargarPerfil = useCallback(async () => {
    setLoading(true);
    const data = await apiFetch(PROFILE_ENDPOINT);
    setPerfil(data || null);
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => {
    if (cargaInicialEjecutada.current) return;
    cargaInicialEjecutada.current = true;
    cargarPerfil();
  }, [cargarPerfil]);

  const getCampo = (obj, nombres) => {
    if (!obj) return "";
    const nombre = nombres.find((key) => obj[key] !== undefined && obj[key] !== null);
    return nombre ? obj[nombre] : "";
  };

  const usuario = getCampo(perfil, ["Usuario", "usuario", "Username", "username"]);
  const correo = getCampo(perfil, ["Correo", "correo", "Email", "email"]);
  const fotoServidor = getCampo(perfil, ["Foto", "foto", "Imagen", "imagen", "Avatar", "avatar", "FotoPerfil", "fotoPerfil"]);

  useEffect(() => {
    if (!perfil) return;
    const fotoActual = fotoServidor || "";
    if (fotoActual) localStorage.setItem(CURRENT_PROFILE_PHOTO_KEY, fotoActual);
    if (!fotoActual) localStorage.removeItem(CURRENT_PROFILE_PHOTO_KEY);
    setFotoPerfil(fotoActual);
    setFotoPreview(fotoActual);
  }, [perfil, fotoServidor]);

  const validarPassword = () => {
    if (!form.passwordActual || !form.passwordNueva || !form.confirmarPassword) {
      return "Completa todos los campos de contrasena.";
    }

    if (form.passwordNueva.length < 8) {
      return "La nueva contrasena debe tener al menos 8 caracteres.";
    }

    if (form.passwordNueva !== form.confirmarPassword) {
      return "La nueva contrasena y la confirmacion no coinciden.";
    }

    if (form.passwordActual === form.passwordNueva) {
      return "La nueva contrasena debe ser diferente a la actual.";
    }

    return "";
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const cargarImagen = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const reducirFoto = async (imageData) => {
    if (imageData.length <= MAX_PROFILE_PHOTO_SIZE) return imageData;

    const img = await cargarImagen(imageData);
    const ratio = Math.min(MAX_PROFILE_PHOTO_WIDTH / img.width, MAX_PROFILE_PHOTO_HEIGHT / img.height, 1);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * ratio));
    canvas.height = Math.max(1, Math.round(img.height * ratio));

    const context = canvas.getContext("2d");
    context.drawImage(img, 0, 0, canvas.width, canvas.height);

    let quality = 0.92;
    let compressed = canvas.toDataURL("image/jpeg", quality);

    while (compressed.length > MAX_PROFILE_PHOTO_SIZE && quality > 0.45) {
      quality -= 0.08;
      compressed = canvas.toDataURL("image/jpeg", quality);
    }

    if (compressed.length <= MAX_PROFILE_PHOTO_SIZE) return compressed;

    const smallerCanvas = document.createElement("canvas");
    smallerCanvas.width = Math.max(1, Math.round(canvas.width * 0.75));
    smallerCanvas.height = Math.max(1, Math.round(canvas.height * 0.75));
    const smallerContext = smallerCanvas.getContext("2d");
    smallerContext.drawImage(canvas, 0, 0, smallerCanvas.width, smallerCanvas.height);

    return smallerCanvas.toDataURL("image/jpeg", 0.65);
  };

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMensaje({ tipo: "danger", texto: "Selecciona un archivo de imagen valido." });
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const imageData = String(reader.result || "");
        const img = await cargarImagen(imageData);
        setFotoEditor({
          src: imageData,
          width: img.width,
          height: img.height,
          zoom: 1,
          offsetX: 0,
          offsetY: 0
        });
      } catch {
        setMensaje({ tipo: "danger", texto: "No fue posible procesar la imagen seleccionada." });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleEditorPointerDown = (e) => {
    if (!fotoEditor) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setEditorDrag({
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: fotoEditor.offsetX,
      offsetY: fotoEditor.offsetY
    });
  };

  const handleEditorPointerMove = (e) => {
    if (!editorDrag || editorDrag.pointerId !== e.pointerId) return;

    setFotoEditor((actual) =>
      clampPhotoEditor({
        ...actual,
        offsetX: editorDrag.offsetX + e.clientX - editorDrag.startX,
        offsetY: editorDrag.offsetY + e.clientY - editorDrag.startY
      })
    );
  };

  const handleEditorPointerUp = (e) => {
    if (editorDrag?.pointerId === e.pointerId) {
      setEditorDrag(null);
    }
  };

  const handleZoomChange = (e) => {
    const zoom = Number(e.target.value);
    setFotoEditor((actual) => clampPhotoEditor({ ...actual, zoom }));
  };

  const crearFotoRecortada = async () => {
    const img = await cargarImagen(fotoEditor.src);
    const canvas = document.createElement("canvas");
    canvas.width = PHOTO_EXPORT_SIZE;
    canvas.height = PHOTO_EXPORT_SIZE;

    const context = canvas.getContext("2d");
    const baseScale = Math.max(PHOTO_EXPORT_SIZE / img.width, PHOTO_EXPORT_SIZE / img.height);
    const scale = baseScale * fotoEditor.zoom;
    const drawWidth = img.width * scale;
    const drawHeight = img.height * scale;
    const offsetScale = PHOTO_EXPORT_SIZE / PHOTO_EDITOR_SIZE;
    const dx = (PHOTO_EXPORT_SIZE - drawWidth) / 2 + fotoEditor.offsetX * offsetScale;
    const dy = (PHOTO_EXPORT_SIZE - drawHeight) / 2 + fotoEditor.offsetY * offsetScale;

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, PHOTO_EXPORT_SIZE, PHOTO_EXPORT_SIZE);
    context.drawImage(img, dx, dy, drawWidth, drawHeight);

    return canvas.toDataURL("image/jpeg", 0.92);
  };

  const guardarFotoEditada = async () => {
    if (!fotoEditor) return;

    try {
      setGuardandoFoto(true);
      const fotoRecortada = await crearFotoRecortada();
      const fotoReducida = await reducirFoto(fotoRecortada);
      const resp = await apiFetch(UPDATE_PHOTO_ENDPOINT, "POST", {
        fotoPerfil: fotoReducida
      });
      setGuardandoFoto(false);

      if (!resp?.ok) {
        setMensaje({
          tipo: "danger",
          texto: resp?.mensaje || resp?.error || "No fue posible guardar la foto en la base de datos."
        });
        return;
      }

      const fotoGuardada = resp.fotoPerfil || resp.FotoPerfil || resp.foto || resp.Foto || fotoReducida;
      localStorage.setItem(CURRENT_PROFILE_PHOTO_KEY, fotoGuardada);
      setFotoPerfil(fotoGuardada);
      setFotoPreview(fotoGuardada);
      setFotoEditor(null);
      window.dispatchEvent(
        new CustomEvent("profile-photo-updated", {
          detail: {
            photo: fotoGuardada
          }
        })
      );
      setMensaje({ tipo: "success", texto: resp.mensaje || "Foto de perfil guardada correctamente." });
    } catch {
      setGuardandoFoto(false);
      setMensaje({ tipo: "danger", texto: "No fue posible procesar la imagen seleccionada." });
    }
  };

  const eliminarFoto = async () => {
    setGuardandoFoto(true);
    const resp = await apiFetch(DELETE_PHOTO_ENDPOINT, "POST");
    setGuardandoFoto(false);

    if (!resp?.ok) {
      setMensaje({
        tipo: "danger",
        texto: resp?.mensaje || resp?.error || "No fue posible eliminar la foto en la base de datos."
      });
      return;
    }

    localStorage.removeItem(CURRENT_PROFILE_PHOTO_KEY);
    setFotoPerfil("");
    setFotoPreview("");
    window.dispatchEvent(
      new CustomEvent("profile-photo-updated", {
        detail: {
          photo: ""
        }
      })
    );
    setMensaje({ tipo: "success", texto: resp.mensaje || "Foto de perfil eliminada." });
  };

  const limpiarFormulario = () => {
    setForm({ passwordActual: "", passwordNueva: "", confirmarPassword: "" });
  };

  const cambiarPassword = async (e) => {
    e.preventDefault();
    setMensaje(null);

    const errorValidacion = validarPassword();
    if (errorValidacion) {
      setMensaje({ tipo: "danger", texto: errorValidacion });
      return;
    }

    setGuardando(true);
    const resp = await apiFetch(CHANGE_PASSWORD_ENDPOINT, "POST", {
      passwordActual: form.passwordActual,
      passwordNueva: form.passwordNueva
    });
    setGuardando(false);

    if (resp?.ok) {
      setMensaje({ tipo: "success", texto: resp.mensaje || "Contrasena actualizada correctamente." });
      limpiarFormulario();
      return;
    }

    setMensaje({
      tipo: "danger",
      texto: resp?.mensaje || resp?.error || "No fue posible actualizar la contrasena."
    });
  };

  return (
    <div className="container-fluid mt-3 px-3 px-md-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
        <div>
          <h2>Mi perfil</h2> 
        </div>
      </div>

      <AlertMessage message={mensaje} onClose={() => setMensaje(null)} />

      {fotoEditor && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ajustar foto de perfil</h5>
                <button type="button" className="btn-close" onClick={() => setFotoEditor(null)} disabled={guardandoFoto}></button>
              </div>
              <div className="modal-body">
                <div
                  className="profile-photo-editor"
                  onPointerDown={handleEditorPointerDown}
                  onPointerMove={handleEditorPointerMove}
                  onPointerUp={handleEditorPointerUp}
                  onPointerCancel={handleEditorPointerUp}
                >
                  <img
                    src={fotoEditor.src}
                    alt="Vista previa de foto"
                    draggable="false"
                    style={{
                      transform: `translate(${fotoEditor.offsetX}px, ${fotoEditor.offsetY}px) scale(${fotoEditor.zoom})`
                    }}
                  />
                </div>
                <div className="mt-3">
                  <label className="form-label">Zoom</label>
                  <input
                    type="range"
                    className="form-range"
                    min="1"
                    max="3"
                    step="0.05"
                    value={fotoEditor.zoom}
                    onChange={handleZoomChange}
                    disabled={guardandoFoto}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFotoEditor(null)} disabled={guardandoFoto}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-success btn-sm" onClick={guardarFotoEditada} disabled={guardandoFoto}>
                  {guardandoFoto ? "Guardando..." : "Guardar foto"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row g-3">
        <div className="col-12 col-lg-5">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title">Datos de usuario</h5>
              {loading ? (
                <p className="text-muted mb-0">Cargando perfil...</p>
              ) : (
                <div className="profile-summary">
                  <div className="profile-photo-panel">
                    <div className="profile-avatar profile-avatar-photo">
                      {fotoPreview ? (
                        <img src={fotoPreview} alt="Foto de perfil" />
                      ) : (
                        String(usuario || "U").charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="profile-photo-actions">
                      <label className="btn btn-outline-primary btn-sm mb-0" htmlFor="profilePhoto">
                        {guardandoFoto ? "Guardando..." : "Subir foto"}
                      </label>
                      <input
                        id="profilePhoto"
                        type="file"
                        className="visually-hidden"
                        accept="image/*"
                        onChange={handleFotoChange}
                        disabled={guardandoFoto}
                      />
                      {fotoPerfil && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={eliminarFoto} disabled={guardandoFoto}>
                          Eliminar
                        </button>
                      )}
                    </div> 
                  </div>
                  <dl className="row mb-0">
                    <dt className="col-sm-4">Usuario</dt>
                    <dd className="col-sm-8">{usuario || "No disponible"}</dd>
                    <dt className="col-sm-4">Correo</dt>
                    <dd className="col-sm-8">{correo || "No disponible"}</dd>
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-7">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Cambiar contrasena</h5>
              <form onSubmit={cambiarPassword} className="row g-3">
                <div className="col-12">
                  <label className="form-label">Contrasena actual</label>
                  <div className="input-group">
                    <input
                      type={showActual ? "text" : "password"}
                      name="passwordActual"
                      className="form-control"
                      value={form.passwordActual}
                      onChange={handleChange}
                      autoComplete="current-password"
                      required
                    />
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowActual((v) => !v)}>
                      {showActual ? "Ocultar" : "Ver"}
                    </button>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Nueva contrasena</label>
                  <div className="input-group">
                    <input
                      type={showNueva ? "text" : "password"}
                      name="passwordNueva"
                      className="form-control"
                      value={form.passwordNueva}
                      onChange={handleChange}
                      autoComplete="new-password"
                      required
                    />
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowNueva((v) => !v)}>
                      {showNueva ? "Ocultar" : "Ver"}
                    </button>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Confirmar nueva contrasena</label>
                  <input
                    type={showNueva ? "text" : "password"}
                    name="confirmarPassword"
                    className="form-control"
                    value={form.confirmarPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                    required
                  />
                </div>

                <div className="col-12 d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarFormulario}>
                    Limpiar
                  </button>
                  <button type="submit" className="btn btn-success btn-sm" disabled={guardando}>
                    {guardando ? "Actualizando..." : "Actualizar contrasena"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
